// @workflow tasks.show-in-3d
/**
 * Show tasks in 3D (capability 20): the single implementation behind every "show in 3D" on the
 * task board (Kanban card and status column, Gantt row, List row, Isolate All, the charts' legend)
 * and behind the UI tool `ui.tasks.show_in_3d`.
 *
 * It resolves the tasks to their effective tags (direct tags, monitoring scopes and anomaly
 * components, server-side through the batch isolation endpoint), records them as the isolated
 * tasks, and opens the board's viewer panel on those tags, on the tab the shared task resolver
 * picks (3D, or 360 for tags placed only there). The panel itself is the board's
 * (taskBoardSurface.js); the per-tab viewer steps are in taskViewerPanel.js.
 */
import {
  prefetchTasksIsolation,
  getTaskIsolationInfo,
  getTaskEffectiveTagIds,
  getIsolateClickBehavior,
  navigateToFacility,
} from '@Task/utils/taskIsolation';
import { error as debugError } from '@/utils/debugLogger';
import { SHOW_TASKS_MODE, SHOW_TASKS_STATUS, TASK_SKIP_REASON } from './taskContract';
import { taskBoardSurface } from './taskBoardSurface';
import { targetTabFor } from './taskViewerPlan';

/** The app's viewer type while the task viewer is open (store SET_VIEWER_TYPE). */
const VIEWER_TYPE_3D = '3d';
/** ViewerTaskColoring's "colour by status" option (store viewers.js SET_TASK_VIEWER_COLORING_OPTION). */
const COLORING_BY_STATUS = 'status';
/** getIsolateClickBehavior: the tags are also in other facilities, so the user chooses. */
const CLICK_CHOOSE_FACILITY = 'navigate-menu';

const sameId = (a, b) => String(a).toLowerCase() === String(b).toLowerCase();
const nothingShown = (skipped) => ({ status: SHOW_TASKS_STATUS.NOTHING, shown: [], skipped });
const skippedAll = (tasks) => tasks.map((task) => ({ id: task.id, reason: TASK_SKIP_REASON.NO_TAGS }));

/**
 * The tasks asked for, as task objects: `tasks` as given (one or many), or `taskIds` looked up on
 * the board first (so their direct tags are known), else `{ id }` for the server to resolve.
 * Unsaved tasks (no id) are left out.
 */
function taskListOf(store, { tasks, taskIds }) {
  if (tasks !== undefined) {
    const list = Array.isArray(tasks) ? tasks : [tasks];
    return list.filter((task) => task && task.id);
  }
  const onBoard = store.getters['TASK/GET_TASKS'] || [];
  return (Array.isArray(taskIds) ? taskIds : [])
    .filter(Boolean)
    .map((id) => onBoard.find((task) => task && sameId(task.id, id)) || { id });
}

/** One task's isolation plan; a failed lookup counts as none, so its direct tags still work. */
async function isolationInfoOf(task, store) {
  try {
    return await getTaskIsolationInfo(task, store);
  } catch (err) {
    debugError('TaskBoard: task isolation lookup failed', err);
    return null;
  }
}

/**
 * The tasks' isolation plans and effective tags, from one batched server call.
 * @returns {Promise<{ shown: object[], skipped: Array<{ id, reason }>, infoByTaskId: object, tagIds: string[] }>}
 */
export async function resolveTasksForViewer(store, tasks) {
  try {
    await prefetchTasksIsolation(tasks, store);
  } catch (err) {
    debugError('TaskBoard: task isolation prefetch failed', err);
  }
  const entries = await Promise.all(tasks.map(async (task) => [task.id, await isolationInfoOf(task, store)]));
  const infoByTaskId = Object.fromEntries(entries.filter(([, info]) => !!info));
  const tagIdsOf = (task) => getTaskEffectiveTagIds(task, infoByTaskId[task.id]);
  const shown = tasks.filter((task) => tagIdsOf(task).length > 0);
  const skipped = skippedAll(tasks.filter((task) => tagIdsOf(task).length === 0));
  return { shown, skipped, infoByTaskId, tagIds: [...new Set(shown.flatMap(tagIdsOf))] };
}

/**
 * Full tags (children → dbIds, point and position for 2D and 360). If the fetch fails the tasks'
 * summary tags are used, so the viewer still opens; isolation stays partial until a retry.
 */
async function fullTagsOf(store, tagIds, tasks) {
  try {
    const tags = await store.dispatch('GET_BASE_TAGS_BY_IDS', tagIds);
    return Array.isArray(tags) ? tags : [];
  } catch (err) {
    debugError('TaskBoard: GET_BASE_TAGS_BY_IDS failed', err);
    return tasks.flatMap((task) => task.forgeTags ?? []);
  }
}

/** What every show does first, before anything is resolved (the board's buttons did it so). */
function commitShowSettings(store, { mode, statusId }) {
  store.commit('SET_VIEWER_TYPE', VIEWER_TYPE_3D);
  if (mode === SHOW_TASKS_MODE.STATUS) store.commit('SET_TASK_VIEWER_COLORING_OPTION', COLORING_BY_STATUS);
  if (statusId !== undefined) store.commit('TASK/SET_LAST_ISOLATED_STATUS_ID', statusId);
}

/**
 * Show tasks on the task board's viewer.
 * @param {{ store: object, surface?: object }} ctx `surface` defaults to the board on screen
 * @param {{
 *   tasks?: object|object[], taskIds?: string[],   the tasks (objects from the board, or ids)
 *   mode?: string,                 a SHOW_TASKS_MODE value (default ISOLATE)
 *   statusId?: string|null,        the status column shown (null: a set of tasks, not a status;
 *                                  absent: leave the remembered status as it is)
 * }} input
 * @returns {Promise<{ status: string, shown: string[], skipped: Array<{ id: string, reason: string }>, tab?: string }>}
 */
export async function showTasksInViewer({ store, surface = taskBoardSurface() }, input = {}) {
  if (!surface) return { status: SHOW_TASKS_STATUS.NO_BOARD, shown: [], skipped: [] };
  commitShowSettings(store, input);
  const tasks = taskListOf(store, input);
  if (!tasks.length) return nothingShown([]);
  const resolved = await resolveTasksForViewer(store, tasks);
  surface.setIsolationInfo(resolved.infoByTaskId);
  if (!resolved.shown.length) return nothingShown(resolved.skipped);
  store.commit('TASK/SET_ISOLATED_TASKS', resolved.shown.map((task) => task.id));
  const tags = await fullTagsOf(store, resolved.tagIds, resolved.shown);
  if (!tags.length) return nothingShown([...skippedAll(resolved.shown), ...resolved.skipped]);
  const tab = targetTabFor(store, tags);
  surface.present({ tags, tab });
  return { status: SHOW_TASKS_STATUS.SHOWN, shown: resolved.shown.map((task) => task.id), skipped: resolved.skipped, tab };
}

/**
 * Each task id with its effective tag ids, in the order asked: what the UI tool isolates when no
 * task board is on screen.
 * @returns {Promise<Array<{ id: string, tagIds: string[] }>>}
 */
export async function tasksWithTagIds(store, { taskIds }) {
  const tasks = taskListOf(store, { taskIds });
  const { infoByTaskId } = await resolveTasksForViewer(store, tasks);
  return tasks.map((task) => ({ id: task.id, tagIds: getTaskEffectiveTagIds(task, infoByTaskId[task.id]) }));
}

/** Open the task's tags in another facility, in a new tab (the facility's own deep link). */
export function openTaskInFacility(router, task, info, facilityId) {
  navigateToFacility(router, facilityId, getTaskEffectiveTagIds(task, info), info);
}

/**
 * Let the user pick where to see a task whose tags are in several facilities: here (shown on the
 * board) or another facility (opened in a new tab).
 * @param {{ store, router, surface? }} ctx
 * @param {object} task
 * @param {{ info: object, chooseFacility: (task, info) => Promise<{ cancelled?, isCurrent?, facilityId? }>, beforeShow?: Function }} options
 */
export async function chooseFacilityAndShow(ctx, task, { info, chooseFacility, beforeShow }) {
  return followChoice(ctx, task, { info, choice: await chooseFacility(task, info), beforeShow });
}

function followChoice(ctx, task, { info, choice, beforeShow }) {
  if (!choice || choice.cancelled) return { status: SHOW_TASKS_STATUS.CANCELLED, shown: [], skipped: [] };
  if (choice.isCurrent) return showOneTask(ctx, task, beforeShow);
  openTaskInFacility(ctx.router, task, info, choice.facilityId);
  return { status: SHOW_TASKS_STATUS.OPENED_ELSEWHERE, shown: [], skipped: [] };
}

function showOneTask(ctx, task, beforeShow) {
  if (typeof beforeShow === 'function') beforeShow();
  return showTasksInViewer(ctx, { tasks: [task], statusId: null });
}

/**
 * One task's 3D button. Tags only here: shown at once. Tags also in other facilities: the user
 * chooses (`chooseFacility`, the board's facility picker). A failed lookup or picker still shows
 * the task here (Kanban and Gantt did so).
 * @param {{ store, router, surface? }} ctx
 * @param {object} task
 * @param {{ info?: object|null, chooseFacility?: Function, beforeShow?: Function }} [options]
 *        `info`: the isolation plan when the caller already has it
 */
export async function isolateTask(ctx, task, { info = null, chooseFacility = null, beforeShow = null } = {}) {
  let asked = null;
  try {
    const isolationInfo = info || await getTaskIsolationInfo(task, ctx.store);
    if (chooseFacility && getIsolateClickBehavior(isolationInfo) === CLICK_CHOOSE_FACILITY) {
      asked = { info: isolationInfo, choice: await chooseFacility(task, isolationInfo) };
    }
  } catch (err) {
    debugError('TaskBoard: task isolation lookup or facility choice failed', err);
    return showOneTask(ctx, task, beforeShow);
  }
  return asked ? followChoice(ctx, task, { ...asked, beforeShow }) : showOneTask(ctx, task, beforeShow);
}
